import { useEffect, useMemo, useState } from 'react';
import { buildLeadCountsIds, getLeadCounts, readLeadCounts } from '../services/leadsService';

/**
 * Badges “Leads y consultas” (F2): **un** `GET /getleadcounts` para todas
 * las listas visibles del espacio (comunes + CAD, ≤50), con caché corta y
 * pedidos deduplicados entre montajes. Contrato: FRONTEND_LEADS.md §
 * GET /getleadcounts. Map: docs/maps/leads.md § Leads y consultas de la lista.
 *
 * `status`:
 * - `ok`: hay contadores (`getCounts(kind, id)`; un id ajeno → null).
 * - `forbidden`: 403 (editor solo de lista / no miembro) → Consultas.
 * - `unavailable`: 404 (ruta todavía sin Gateway), 400, 5xx o red → Consultas.
 * - `idle` / `loading`: todavía no hay datos → Consultas (sin parpadeo de badge).
 *
 * `invalidateCollectionLeadCounts()` (p. ej. después de
 * `markleadinquiriesseen`) vacía la caché y hace que los montados vuelvan a
 * pedir.
 */

const CACHE_TTL_MS = 60 * 1000;
const cache = new Map();
const inflight = new Map();
const listeners = new Set();
let apiAvailable = false;

/** ¿Algún endpoint de F1/F2 respondió OK en esta sesión? (gatea `via=lot_share`). */
export function isLeadCollectionsApiAvailable() {
  return apiAvailable;
}

/** `getleads` con `list_stats` o `getleadcounts` OK: la API ya tiene F1–F3. */
export function markLeadCollectionsApiAvailable() {
  apiAvailable = true;
}

/** Vacía la caché de contadores y avisa a los hooks montados. */
export function invalidateCollectionLeadCounts() {
  cache.clear();
  listeners.forEach((fn) => fn());
}

/** Solo tests. */
export function resetCollectionLeadCountsForTests() {
  cache.clear();
  inflight.clear();
  listeners.clear();
  apiAvailable = false;
}

function toResult(res) {
  const counts = readLeadCounts(res);
  if (counts) {
    markLeadCollectionsApiAvailable();
    return { status: 'ok', ...counts };
  }
  if (res?._httpStatus === 403) return { status: 'forbidden' };
  return { status: 'unavailable' };
}

async function loadCounts(key, ids, ownerUserId) {
  const hit = cache.get(key);
  if (hit && Date.now() - hit.at < CACHE_TTL_MS) return hit.result;
  if (inflight.has(key)) return inflight.get(key);
  const promise = (async () => {
    let result;
    try {
      result = toResult(await getLeadCounts(ids, { ownerUserId }));
    } catch (err) {
      console.error('Leads y consultas: GET /getleadcounts falló', err);
      result = { status: 'unavailable' };
    }
    cache.set(key, { at: Date.now(), result });
    return result;
  })();
  inflight.set(key, promise);
  try {
    return await promise;
  } finally {
    inflight.delete(key);
  }
}

const IDLE = { status: 'idle' };

/**
 * @param {{ listIds?: string[], projectIds?: string[], ownerUserId?: string|null, enabled?: boolean }} input
 * @returns {{ status: string, getCounts: (kind: 'list'|'showroom', id: string) =>
 *   ({ leads: number, newLeads: number, unseenLeads: number }|null) }}
 */
export function useCollectionLeadCounts({ listIds = [], projectIds = [], ownerUserId = null, enabled = true } = {}) {
  const ids = buildLeadCountsIds({ listIds, projectIds });
  const owner = ownerUserId ? String(ownerUserId).trim() : '';
  const key = `${owner}|${ids.listIds.join(',')}|${ids.projectIds.join(',')}`;
  const hasIds = ids.listIds.length + ids.projectIds.length > 0;
  const active = enabled && hasIds;
  const [state, setState] = useState(IDLE);
  const [seq, setSeq] = useState(0);

  useEffect(() => {
    const onInvalidate = () => setSeq((n) => n + 1);
    listeners.add(onInvalidate);
    return () => listeners.delete(onInvalidate);
  }, []);

  useEffect(() => {
    if (!active) {
      setState(IDLE);
      return undefined;
    }
    let alive = true;
    const [, listPart, projectPart] = key.split('|');
    const requestIds = {
      listIds: listPart ? listPart.split(',') : [],
      projectIds: projectPart ? projectPart.split(',') : [],
    };
    setState((prev) => (prev.key === key ? prev : { status: 'loading', key }));
    loadCounts(key, requestIds, owner || null).then((result) => {
      if (alive) setState({ ...result, key });
    });
    return () => {
      alive = false;
    };
  }, [active, key, owner, seq]);

  return useMemo(() => ({
    status: state.status,
    getCounts: (kind, id) => {
      if (state.status !== 'ok') return null;
      const lookup = String(id || '').trim().toLowerCase();
      if (!lookup) return null;
      const map = kind === 'showroom' ? state.projects : state.lists;
      return map?.[lookup] || null;
    },
  }), [state]);
}
